import { Box, Flex, Text } from '@chakra-ui/react';
import { Cart } from '@diamond/shared/types';
import { getItemCardTrackingParams, trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS } from '@diamond/sol-buyer/constant';
import type { UseNoteInputReturn } from '@diamond/sol-buyer/utils';
import { getCartItemStockInfo } from '@diamond/sol-buyer/utils';
import Link from 'next/link';

import { Image } from '../../common';
import { CartItemNoteEditor } from './CartItemNoteEditor';
import { CartItemNoteToggle } from './CartItemNoteToggle';

export const CartItemImage = ({
  imageUrl,
  alt,
  productId,
  item,
  size = { base: '64px', md: '112px' },
}: {
  imageUrl?: string;
  alt: string;
  productId: string;
  item: Cart;
  size?: { base: string; md: string };
}) => {
  const handleClick = () => {
    trackEvent(TRACKING_EVENTS.CLICK_PRODUCT_CART, {
      ...getItemCardTrackingParams(
        item.product,
        item.quantity / 1000,
        'cart',
        'cart'
      ),
      section_name: 'cart',
    });
  };

  return (
    <Link href={`/product/${productId}`} passHref legacyBehavior>
      <Box
        as="a"
        position="relative"
        minWidth={{ base: '64px', lg: '112px' }}
        minHeight={{ base: '64px', lg: '112px' }}
        maxWidth={{ base: '64px', lg: '112px' }}
        maxHeight={{ base: '64px', lg: '112px' }}
        width={size}
        height={size}
        rounded="lg"
        border={{ base: '1px solid #F3F4F5', md: '2px solid #F3F4F5' }}
        overflow="hidden"
        cursor="pointer"
        onClick={handleClick}
        _hover={{ opacity: 0.8 }}
        transition="opacity 0.2s"
      >
        <Image
          src={imageUrl}
          alt={alt}
          style={{ objectFit: 'scale-down' }}
          fill
        />
      </Box>
    </Link>
  );
};

export const ProductInfo = ({
  item,
  productId,
}: {
  item: Cart;
  productId: string;
}) => {
  const stockInfo = getCartItemStockInfo(item);
  const isDisabled = stockInfo.isEmptyStock;

  const handleClick = () => {
    trackEvent(TRACKING_EVENTS.CLICK_PRODUCT_CART, {
      ...getItemCardTrackingParams(
        item.product,
        item.quantity / 1000,
        'cart',
        'cart'
      ),
      section_name: 'cart',
    });
  };

  return (
    <Link href={`/product/${productId}`} passHref legacyBehavior>
      <Box
        as="a"
        cursor="pointer"
        onClick={handleClick}
        _hover={{ opacity: 0.8 }}
        transition="opacity 0.2s"
      >
        <Text
          fontSize={{ base: '10px', lg: 'xs' }}
          fontWeight="semibold"
          color={isDisabled ? '#8E8E93' : '#131415'}
          mb={{ base: '0px', lg: '4px' }}
          flexShrink={0}
          display={{ base: 'none', lg: 'block' }}
        >
          {item.product.brand_desc || item.product.brand_name}{' '}
        </Text>
        {/* Mobile Item */}
        <Text
          display={{ base: 'block', lg: 'none' }}
          fontSize="12px"
          noOfLines={2}
        >
          {' '}
          {item.product.brand_desc || item.product.brand_name} •{' '}
          {item.product.title || item.product.desc}
        </Text>
        {/* End Mobile Item */}
        <Flex
          flexDir={{ base: 'column', lg: 'column' }}
          mb={{ base: '0px', lg: '4px' }}
          display={{ base: 'none', lg: 'flex' }}
        >
          <Text
            fontSize={{ base: 'xs', lg: 'sm' }}
            color={isDisabled ? '#8E8E93' : '#131415'}
          >
            {item.product.title || item.product.desc}
          </Text>
          <Text
            display={{ base: 'none', lg: 'block' }}
            fontSize="xs"
            textColor={isDisabled ? '#8E8E93' : '#999999'}
          >
            Pak: {item.product.pack_unit}
          </Text>
          <Text
            fontSize="10px"
            display={{ base: 'none', lg: 'block' }}
            color={isDisabled ? '#8E8E93' : '#999999'}
          >
            {item.product.item_code}
          </Text>
        </Flex>
      </Box>
    </Link>
  );
};

export const CartItemInfo = ({
  item,
  noteProps,
}: {
  item: Cart;
  noteProps: {
    isNoteFieldVisible: boolean;
    openNoteEditor: () => void;
    cancelNoteEdit: () => void;
    confirmNoteEdit: () => void;
    savedNote: string;
    currentNote: string;
    noteInputProps: UseNoteInputReturn['inputProps'];
    charCount: number;
  };
}) => {
  const stockInfo = getCartItemStockInfo(item);
  const isDisabled = stockInfo.isEmptyStock;

  const {
    isNoteFieldVisible,
    openNoteEditor,
    cancelNoteEdit,
    confirmNoteEdit,
    savedNote,
    noteInputProps,
    charCount,
  } = noteProps;

  return (
    <Flex
      flexDir={{ base: 'row', lg: 'column' }}
      justifyContent="center"
      maxW={{ base: '100%', lg: '250px' }}
      w="full"
      overflow="hidden"
      minWidth={0}
    >
      <ProductInfo item={item} productId={item.product.id} />
      {!isNoteFieldVisible ? (
        <CartItemNoteToggle
          isDisabled={isDisabled}
          onToggle={openNoteEditor}
          note={savedNote}
        />
      ) : null}
      {isNoteFieldVisible ? (
        <CartItemNoteEditor
          noteInputProps={noteInputProps}
          charCount={charCount}
          onCancel={cancelNoteEdit}
          onConfirm={confirmNoteEdit}
        />
      ) : null}
    </Flex>
  );
};
